import { HttpHandler, HttpInterceptor, HttpRequest, HttpEvent, HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthenticationService } from "./authentication.service";
import { Observable, from } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { SpinnerService } from '../../spinner/spinner.service';
import { EncryptionService } from "./encryption.service";
import { environment } from "src/environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    constructor(
        private _authService: AuthenticationService,
        private _spinnerService: SpinnerService,
        private _encryptionService: EncryptionService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        
        // If the request has the 'skip-authorization' header, don't add the Authorization header
        if (req.headers.has('skip-authorization')) {
            const modifiedReq = req.clone({
                headers: req.headers.delete('skip-authorization') // Remove the custom header
            });
            return next.handle(modifiedReq); // Pass the request without Authorization
        }

        // Handle the encryption logic synchronously inside an Observable
        return from(this.encryptRequestHeaders(req)).pipe(
            switchMap((modifiedReq) => {
                const started = Date.now();
                this._spinnerService.show();

                return next.handle(modifiedReq).pipe(
                    tap(
                        (event: HttpEvent<any>) => {
                            if (event instanceof HttpResponse) {
                                this._spinnerService.hide();
                            }
                        },
                        (err: any) => {
                            if (err instanceof HttpErrorResponse) {
                                this._spinnerService.hide();
                            }
                        }
                    )
                );
            })
        );
    }

    // Separate method to handle AES key generation and RSA encryption
    private async encryptRequestHeaders(req: HttpRequest<any>): Promise<HttpRequest<any>> {
        try {
            
            // Only encrypt if encryption is enabled in the environment
            if (!environment.encryptapiUrl) {
                return req;
            }

            // Generate Encrypt the AES key using RSA
            const { hex: AESEncryptedKeyHex } = await this._encryptionService.encryptAESKeyWithRSA();
            //console.log('AESEncryptedKey (Hex):', AESEncryptedKeyHex);
    
            // Add the encrypted AES key to the headers
            let modifiedHeaders = req.headers.set('Security-Token', AESEncryptedKeyHex);

            // Bypass ngrok proxy
            if (req.url.includes('ngrok')) {
                modifiedHeaders = modifiedHeaders.set('ngrok-skip-browser-warning', 'true');
            }
    
            //console.log(modifiedHeaders, "Modified Headers");
    
            // Add the Authorization header
            const authToken = this._authService.getToken();
            if (authToken) {
                modifiedHeaders = modifiedHeaders.set('Api-Token', `${authToken}`);
            }
    
            // Return the modified request with updated headers
            return req.clone({ headers: modifiedHeaders });
        } catch (error) {
            console.error('Error in encryptRequestHeaders:', error);
            throw error; // Let it propagate for debugging purposes
        }
    }   
}